import { Checkbox, Grid, Skeleton, Stack, Textarea } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';

import { FieldWarningNote } from '../../form/FieldWarningNote.js';
import { FormSubsection } from '../../ui/FormSubsection.js';
import { RegisterFieldTab } from '../../form/RegisterFieldTab.js';
import type { EnvironmentalDataFormData } from '../../../types/reports.js';

/**
 * Die vier `FieldWarningNote` tragen Ankerpfade, hinter denen KEIN Eingabefeld steht
 * (`noxMeasures`, ...): `EmissionReductionMeasureHint` im Backend meldet dort einen Hinweis,
 * solange in der Gruppe kein Haken gesetzt ist. Der Hinweis gilt der Gruppe, nicht einem
 * einzelnen Kaestchen -- deshalb steht er ueber den Kaestchen und faerbt keines davon ein.
 *
 * Nebenwirkung, die hier erwuenscht ist: ueber diese vier Anker meldet sich der Reiter
 * "Emissionsminderung" ueberhaupt erstmals an der Feld-zu-Reiter-Registry an. Er trug bis dahin
 * auch bei Fehlern keinen Punkt, weil auf ihm ausschliesslich rohe Mantine-Felder stehen und
 * sich nur die geteilten Feldkomponenten selbst anmelden.
 */
interface EmissionsminderungSectionProps {
  form: UseFormReturnType<EnvironmentalDataFormData>;
  disabled?: boolean;
  loading?: boolean;
}

export function EmissionsminderungSection({
  form,
  disabled,
  loading,
}: EmissionsminderungSectionProps) {
  if (loading) {
    return (
      <Grid>
        {[0, 1, 2].map((i) => (
          <Grid.Col span={12} key={i}>
            <Skeleton height={150} />
          </Grid.Col>
        ))}
      </Grid>
    );
  }

  const values = form.getValues();

  return (
    <Stack>
      <FormSubsection title="NOₓ-Minderung">
        <FieldWarningNote field="noxMeasures" />
        <Grid>
          <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
            <Checkbox
              label="Gestufte Verbrennung"
              key={form.key('noxStagedCombustion')}
              {...form.getInputProps('noxStagedCombustion', { type: 'checkbox' })}
              disabled={disabled}
            />
            <RegisterFieldTab field="noxStagedCombustion" label="NOₓ-Minderung: Gestufte Verbrennung" />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
            <Checkbox
              label="SNCR"
              key={form.key('noxSncr')}
              {...form.getInputProps('noxSncr', { type: 'checkbox' })}
              disabled={disabled}
            />
            <RegisterFieldTab field="noxSncr" label="NOₓ-Minderung: SNCR" />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
            <Checkbox
              label="SCR"
              key={form.key('noxScr')}
              {...form.getInputProps('noxScr', { type: 'checkbox' })}
              disabled={disabled}
            />
            <RegisterFieldTab field="noxScr" label="NOₓ-Minderung: SCR" />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
            <Checkbox
              label="Sonstige"
              key={form.key('noxOther')}
              {...form.getInputProps('noxOther', { type: 'checkbox' })}
              disabled={disabled}
            />
            <RegisterFieldTab field="noxOther" label="NOₓ-Minderung: Sonstige" />
          </Grid.Col>
          {/* Immer angemeldet, auch bei ausgeblendetem Textfeld (D-13). */}
          <RegisterFieldTab field="noxOtherDescription" label="Beschreibung sonstiger NOₓ-Maßnahmen" />
          {values.noxOther && (
            <Grid.Col span={12}>
              <Textarea
                label="Beschreibung sonstiger NOₓ-Maßnahmen"
                key={form.key('noxOtherDescription')}
                {...form.getInputProps('noxOtherDescription')}
                disabled={disabled}
                minRows={2}
              />
            </Grid.Col>
          )}
        </Grid>
      </FormSubsection>

      <FormSubsection title="SO₂-Minderung">
        <FieldWarningNote field="so2Measures" />
        <Grid>
          <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
            <Checkbox
              label="Additivzugabe"
              key={form.key('so2Additives')}
              {...form.getInputProps('so2Additives', { type: 'checkbox' })}
              disabled={disabled}
            />
            <RegisterFieldTab field="so2Additives" label="SO₂-Minderung: Additivzugabe" />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
            <Checkbox
              label="Nasswäscher"
              key={form.key('so2WetScrubber')}
              {...form.getInputProps('so2WetScrubber', { type: 'checkbox' })}
              disabled={disabled}
            />
            <RegisterFieldTab field="so2WetScrubber" label="SO₂-Minderung: Nasswäscher" />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
            <Checkbox
              label="Trockensorption"
              key={form.key('so2DryScrubber')}
              {...form.getInputProps('so2DryScrubber', { type: 'checkbox' })}
              disabled={disabled}
            />
            <RegisterFieldTab field="so2DryScrubber" label="SO₂-Minderung: Trockensorption" />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
            <Checkbox
              label="Sonstige"
              key={form.key('so2Other')}
              {...form.getInputProps('so2Other', { type: 'checkbox' })}
              disabled={disabled}
            />
            <RegisterFieldTab field="so2Other" label="SO₂-Minderung: Sonstige" />
          </Grid.Col>
          {/* Immer angemeldet, auch bei ausgeblendetem Textfeld (D-13). */}
          <RegisterFieldTab field="so2OtherDescription" label="Beschreibung sonstiger SO₂-Maßnahmen" />
          {values.so2Other && (
            <Grid.Col span={12}>
              <Textarea
                label="Beschreibung sonstiger SO₂-Maßnahmen"
                key={form.key('so2OtherDescription')}
                {...form.getInputProps('so2OtherDescription')}
                disabled={disabled}
                minRows={2}
              />
            </Grid.Col>
          )}
        </Grid>
      </FormSubsection>

      <FormSubsection title="Staubminderung">
        <FieldWarningNote field="dustMeasures" />
        <Grid>
          <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
            <Checkbox
              label="Schlauchfilter"
              key={form.key('dustReductionBagFilter')}
              {...form.getInputProps('dustReductionBagFilter', { type: 'checkbox' })}
              disabled={disabled}
            />
            <RegisterFieldTab field="dustReductionBagFilter" label="Staubminderung: Schlauchfilter" />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
            <Checkbox
              label="Elektrofilter"
              key={form.key('dustReductionElectrostaticFilter')}
              {...form.getInputProps('dustReductionElectrostaticFilter', { type: 'checkbox' })}
              disabled={disabled}
            />
            <RegisterFieldTab field="dustReductionElectrostaticFilter" label="Staubminderung: Elektrofilter" />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
            <Checkbox
              label="Hybridfilter"
              key={form.key('dustReductionHybridFilter')}
              {...form.getInputProps('dustReductionHybridFilter', { type: 'checkbox' })}
              disabled={disabled}
            />
            <RegisterFieldTab field="dustReductionHybridFilter" label="Staubminderung: Hybridfilter" />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
            <Checkbox
              label="Sonstige"
              key={form.key('dustReductionOther')}
              {...form.getInputProps('dustReductionOther', { type: 'checkbox' })}
              disabled={disabled}
            />
            <RegisterFieldTab field="dustReductionOther" label="Staubminderung: Sonstige" />
          </Grid.Col>
          {/* Immer angemeldet, auch bei ausgeblendetem Textfeld (D-13). */}
          <RegisterFieldTab field="dustReductionOtherDescription" label="Beschreibung sonstiger Staubminderungs-Maßnahmen" />
          {values.dustReductionOther && (
            <Grid.Col span={12}>
              {/* EM-03: bleibt optional (ausdrueckliche Kundenausnahme zur Pflicht-Grundstellung)
                  -- der Haken darueber ist Pflicht, er ist eine Aussage ("keine sonstigen
                  Maßnahmen" ist auch eine), der Freitext nicht. */}
              <Textarea
                label="Beschreibung sonstiger Staubminderungs-Maßnahmen"
                key={form.key('dustReductionOtherDescription')}
                {...form.getInputProps('dustReductionOtherDescription')}
                disabled={disabled}
                minRows={2}
              />
            </Grid.Col>
          )}
        </Grid>
      </FormSubsection>

      <FormSubsection title="Hg (Quecksilber)">
        <FieldWarningNote field="hgMeasures" />
        <Grid>
          <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
            <Checkbox
              label="Filterstaubausschleusung"
              key={form.key('hgFilterDustDischarge')}
              {...form.getInputProps('hgFilterDustDischarge', { type: 'checkbox' })}
              disabled={disabled}
            />
            <RegisterFieldTab field="hgFilterDustDischarge" label="Hg (Quecksilber): Filterstaubausschleusung" />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
            <Checkbox
              label="Aktivkohle"
              key={form.key('hgActivatedCarbon')}
              {...form.getInputProps('hgActivatedCarbon', { type: 'checkbox' })}
              disabled={disabled}
            />
            <RegisterFieldTab field="hgActivatedCarbon" label="Hg (Quecksilber): Aktivkohle" />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
            <Checkbox
              label="Herdofenkoks"
              key={form.key('hgHearthFurnaceCoke')}
              {...form.getInputProps('hgHearthFurnaceCoke', { type: 'checkbox' })}
              disabled={disabled}
            />
            <RegisterFieldTab field="hgHearthFurnaceCoke" label="Hg (Quecksilber): Herdofenkoks" />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
            <Checkbox
              label="Sonstige"
              key={form.key('hgOther')}
              {...form.getInputProps('hgOther', { type: 'checkbox' })}
              disabled={disabled}
            />
            <RegisterFieldTab field="hgOther" label="Hg (Quecksilber): Sonstige" />
          </Grid.Col>
          {/* Immer angemeldet, auch bei ausgeblendetem Textfeld (D-13). */}
          <RegisterFieldTab field="hgOtherDescription" label="Beschreibung sonstiger Hg-Maßnahmen" />
          {values.hgOther && (
            <Grid.Col span={12}>
              <Textarea
                label="Beschreibung sonstiger Hg-Maßnahmen"
                key={form.key('hgOtherDescription')}
                {...form.getInputProps('hgOtherDescription')}
                disabled={disabled}
                minRows={2}
              />
            </Grid.Col>
          )}
        </Grid>
      </FormSubsection>
    </Stack>
  );
}
